<!--
  SuperAdminModal.vue
  예약 슈퍼 담당자 관리 모달
  
  주요 기능:
  - 슈퍼 담당자 목록 조회
  - 신규 관리자 추가
  - 기존 관리자 수정/삭제
  - 인라인 편집 기능
-->
<template>
  <a-modal
    :visible="visible"
    title="슈퍼 담당자 관리"
    width="900px"
    :footer="null"
    @cancel="handleCancel"
    :maskClosable="false"
    :destroyOnClose="true"
  >
    <div class="super-admin-modal">
      <!-- 추가 버튼 -->
      <div class="action-buttons">
        <a-button type="primary" size="small" @click="handleAdd">
          <i class="fa fa-plus"></i>
          추가
        </a-button>
      </div>

      <!-- 테이블 -->
      <a-table
        :columns="columns"
        :data-source="dataSource"
        :loading="loading"
        :pagination="false"
        row-key="_id"
        size="small"
        class="admin-table"
      >
        <template #emptyText>
          <div class="noResult">등록된 슈퍼 담당자가 없습니다.</div>
        </template>
      </a-table>
    </div>
  </a-modal>
</template>

<script>
import { ref, reactive, onMounted, getCurrentInstance, h, watch } from 'vue';
import { message, Input } from 'ant-design-vue';
import { phoneNumberFormatter, convertToLocalTime } from '@/utils/utils';
import { twoBtnDialog } from '@/utils/utils';

export default {
  name: 'SuperAdminModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['cancel'],
  setup(props, { emit }) {
    // API 인스턴스
    const instance = getCurrentInstance();
    const apiInstance = instance.proxy.$apiInstance;

    // 상태 관리
    const loading = ref(false);
    const dataSource = ref([]);
    const editingKey = ref('');
    const editableData = reactive({});

    // 새 관리자 추가
    const handleAdd = () => {
      const newId = `new_${Date.now()}`;
      const newManager = {
        _id: newId,
        managerName: '',
        phoneNumber: '',
        createAt: new Date().toISOString(),
        isNew: true,
        editable: true
      };
      
      dataSource.value.unshift(newManager);
      editingKey.value = newId;
      editableData[newId] = { ...newManager };
    };

    // 수정 시작
    const handleEdit = (record) => {
      editingKey.value = record._id;
      editableData[record._id] = { ...record };
    };

    // 저장
    const handleSave = async (record) => {
      const key = record._id;
      const editedData = editableData[key];
      
      // 유효성 검사
      if (!editedData.managerName || !editedData.managerName.trim()) {
        message.warning('관리자 이름을 입력해주세요.');
        return;
      }
      
      if (!editedData.phoneNumber || !editedData.phoneNumber.trim()) {
        message.warning('전화번호를 입력해주세요.');
        return;
      }
      
      // 전화번호 형식 검사 (숫자만 추출)
      const phoneNumberOnly = editedData.phoneNumber.replace(/[^0-9]/g, '');
      if (phoneNumberOnly.length < 10 || phoneNumberOnly.length > 11) {
        message.warning('올바른 전화번호 형식이 아닙니다.');
        return;
      }
      
      // 전화번호 중복 체크
      const duplicatePhone = dataSource.value.find(item => {
        // 현재 편집 중인 항목은 제외하고 중복 체크
        if (item._id === record._id) return false;
        
        // 신규 항목들은 제외 (서버에 저장되지 않은 임시 항목)
        if (item.isNew && item._id !== record._id) return false;
        
        const existingPhoneOnly = item.phoneNumber ? item.phoneNumber.replace(/[^0-9]/g, '') : '';
        return existingPhoneOnly === phoneNumberOnly;
      });
      
      if (duplicatePhone) {
        message.warning('이미 등록된 전화번호입니다.');
        return;
      }
      
      loading.value = true;
      
      try {
        let response;
        if (record.isNew) {
          // 신규 생성
          const createData = {
            managerName: editedData.managerName.trim(),
            phoneNumber: phoneNumberOnly
          };
          response = await apiInstance.post('/reservation-super-managers', createData);
          
          if (response.status === 201) {
            message.success('슈퍼 담당자가 성공적으로 추가되었습니다.');
            await loadManagers();
          }
        } else {
          // 수정
          const updateData = {
            managerName: editedData.managerName.trim(),
            phoneNumber: phoneNumberOnly
          };
          response = await apiInstance.put(`/reservation-super-managers/${record._id}`, updateData);
          
          if (response.status === 200) {
            message.success('슈퍼 담당자 정보가 수정되었습니다.');
            await loadManagers();
          }
        }
        
        editingKey.value = '';
        delete editableData[key];
      } catch (error) {
        console.error('저장 실패:', error);
        // 백엔드에서 중복 체크 에러가 온 경우
        if (error.response?.status === 400 && error.response?.data?.message?.includes('중복')) {
          message.error('이미 등록된 전화번호입니다.');
        } else {
          message.error(error.response?.data?.message || '저장 중 오류가 발생했습니다.');
        }
      } finally {
        loading.value = false;
      }
    };

    // 취소
    const handleCancelEdit = (record) => {
      const key = record._id;
      
      if (record.isNew) {
        // 신규 행인 경우 삭제
        dataSource.value = dataSource.value.filter(item => item._id !== key);
      }
      
      editingKey.value = '';
      delete editableData[key];
    };

    // 삭제
    const handleDelete = async (record) => {
      if (record.isNew) {
        // 신규 행은 바로 삭제
        dataSource.value = dataSource.value.filter(item => item._id !== record._id);
        return;
      }
      
      const isConfirmed = await twoBtnDialog(
        '슈퍼 담당자 삭제',
        `${record.managerName} 관리자를 삭제하시겠습니까?`,
        '삭제'
      );
      
      if (!isConfirmed) return;
      
      loading.value = true;
      
      try {
        const response = await apiInstance.delete(`/reservation-super-managers/${record._id}`);
        
        if (response.status === 200) {
          message.success('슈퍼 담당자가 삭제되었습니다.');
          await loadManagers();
        }
      } catch (error) {
        console.error('삭제 실패:', error);
        message.error(error.response?.data?.message || '삭제 중 오류가 발생했습니다.');
      } finally {
        loading.value = false;
      }
    };

    // 입력값 변경 처리
    const handleInputChange = (value, key, field) => {
      if (!editableData[key]) {
        editableData[key] = {};
      }
      editableData[key][field] = value;
    };

    // 렌더링 함수들
    const renderIndex = ({ index }) => {
      return dataSource.value.length - index;
    };

    const renderManagerName = ({ record }) => {
      const isEditing = editingKey.value === record._id;
      
      if (isEditing) {
        return h(Input, {
          value: editableData[record._id]?.managerName || '',
          onChange: (e) => handleInputChange(e.target.value, record._id, 'managerName'),
          placeholder: '관리자 이름 입력',
          maxLength: 50
        });
      }
      
      return record.managerName || '-';
    };

    const renderPhoneNumber = ({ record }) => {
      const isEditing = editingKey.value === record._id;
      
      if (isEditing) {
        return h(Input, {
          value: editableData[record._id]?.phoneNumber || '',
          onChange: (e) => handleInputChange(e.target.value, record._id, 'phoneNumber'),
          placeholder: '전화번호 입력 (숫자만)',
          maxLength: 13
        });
      }
      
      return phoneNumberFormatter(record.phoneNumber) || '-';
    };

    const renderCreateAt = ({ record }) => {
      if (record.isNew) return '-';
      return convertToLocalTime(record.createAt);
    };

    const renderActions = ({ record }) => {
      const isEditing = editingKey.value === record._id;
      
      if (isEditing) {
        return h('div', { style: 'display: flex; gap: 8px; justify-content: center;' }, [
          h('a', {
            onClick: () => handleSave(record),
            style: 'color: #1890ff;'
          }, '저장'),
          h('a', {
            onClick: () => handleCancelEdit(record),
            style: 'color: #666;'
          }, '취소')
        ]);
      }
      
      return h('div', { style: 'display: flex; gap: 8px; justify-content: center;' }, [
        h('a', {
          onClick: () => handleEdit(record),
          style: 'color: #1890ff;'
        }, '수정'),
        h('a', {
          onClick: () => handleDelete(record),
          style: 'color: #ff4d4f;'
        }, '삭제')
      ]);
    };

    // 컬럼 정의
    const columns = [
      {
        title: '번호',
        key: 'index',
        width: 60,
        align: 'center',
        customRender: renderIndex
      },
      {
        title: '관리자 이름',
        dataIndex: 'managerName',
        key: 'managerName',
        width: 200,
        align: 'center',
        customRender: renderManagerName
      },
      {
        title: '전화번호',
        dataIndex: 'phoneNumber',
        key: 'phoneNumber',
        width: 150,
        align: 'center',
        customRender: renderPhoneNumber
      },
      {
        title: '생성일',
        dataIndex: 'createAt',
        key: 'createAt',
        width: 180,
        align: 'center',
        customRender: renderCreateAt
      },
      {
        title: '작업',
        key: 'action',
        width: 120,
        align: 'center',
        customRender: renderActions
      }
    ];

    // 관리자 목록 조회
    const loadManagers = async () => {
      loading.value = true;
      
      try {
        const response = await apiInstance.get('/reservation-super-managers');
        
        if (response.status === 200) {
          dataSource.value = response.data || [];
        }
      } catch (error) {
        console.error('관리자 목록 조회 실패:', error);
        message.error('관리자 목록을 불러오는 중 오류가 발생했습니다.');
        dataSource.value = [];
      } finally {
        loading.value = false;
      }
    };

    // 모달 닫기
    const handleCancel = () => {
      editingKey.value = '';
      Object.keys(editableData).forEach(key => {
        delete editableData[key];
      });
      emit('cancel');
    };

    // visible prop 변경 감지
    watch(() => props.visible, (newVal) => {
      if (newVal) {
        loadManagers();
      } else {
        // 모달이 닫힐 때 편집 상태 초기화
        editingKey.value = '';
        Object.keys(editableData).forEach(key => {
          delete editableData[key];
        });
      }
    });

    return {
      loading,
      dataSource,
      columns,
      handleAdd,
      handleCancel
    };
  }
};
</script>

<style lang="scss" scoped>
.super-admin-modal {
  padding: 16px;
  .action-buttons {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 16px;
  }
  
  .admin-table {
    min-height: 400px;
    max-height: 70vh;
    :deep(.ant-table) {
      border: 1px solid #f0f0f0;
      border-radius: 8px;
    }
    
    :deep(.ant-table-thead > tr > th) {
      background-color: #fafafa;
      font-weight: 600;
    }
    
    :deep(.ant-table-tbody > tr) {
      &:hover {
        background-color: #f5f5f5;
      }
    }
  }
  
  .noResult {
    text-align: center;
    padding: 40px;
    color: #999;
  }
}
:deep(.ant-input) {
  &:hover,
  &:focus {
    border-color: #40a9ff;
  }
}
</style> 